import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { pad } from '@/lib/format'
import { Logotype } from '@/components/generals/brand'
import { RollChars, RollText } from '@/components/generals/roll-text'
import { scrollToTarget, useLenis } from '@/components/generals/smooth-scroll'
import { contact } from '@/constants/content'
import { navigation, siteConfig } from '@/constants/site'

const mailSubject = encodeURIComponent("Bashkëpunim me JAP")

export const Footer: FC = (): ReactNode => {
    const { pathname } = useRouter()
    const isHome = pathname === '/'
    const lenis = useLenis()

    const hrefFor = (hash: string): string => (isHome ? hash : `/${hash}`)

    return (
        <footer
            id='kontakt'
            data-tone='ink'
            aria-labelledby='kontakt-titulli'
            className='relative overflow-hidden px-5 pt-28 md:px-8 md:pt-40 lg:px-12'
        >
            <div className='grid grid-cols-12 gap-x-5 gap-y-6'>
                <p className='col-span-12 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted md:col-span-4'>
                    <span className='font-heading tabular-nums'>08</span>
                    <span aria-hidden='true' className='h-px w-8 bg-tone-line' />
                    {contact.label}
                </p>

                <h2
                    id='kontakt-titulli'
                    className='col-span-12 font-serif text-4xl italic leading-[1.05] md:col-span-8 md:text-6xl lg:text-7xl'
                >
                    {contact.question}
                </h2>
            </div>

            <p className='mt-12 text-sm text-tone-muted md:mt-20'>
                {contact.prompt}
            </p>

            <a
                href={`mailto:${siteConfig.email}?subject=${mailSubject}`}
                aria-label={`Na shkruani në ${siteConfig.email}`}
                className='group mt-3 block w-fit font-heading text-[9vw] font-bold leading-[1.2] tracking-[-0.045em]'
            >
                <RollChars text={siteConfig.email} />
            </a>

            <div className='mt-16 grid grid-cols-2 gap-x-5 gap-y-12 border-t border-tone-line pt-10 md:mt-24 md:grid-cols-12'>
                <div className='col-span-2 md:col-span-4'>
                    <Logotype className='h-8 w-auto' chevronClassName='text-tone-accent' />

                    <p className='mt-5 max-w-xs text-sm leading-relaxed text-tone-muted'>
                        {siteConfig.tagline}
                    </p>
                </div>

                <nav aria-label="Seksionet" className='md:col-span-3 md:col-start-6'>
                    <p className='text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted'>
                        Seksionet
                    </p>

                    <ul className='mt-4 flex flex-col gap-2 text-sm font-medium'>
                        {navigation.map((item, index) => (
                            <li key={item.href}>
                                <a href={hrefFor(item.href)} className='group inline-flex items-baseline gap-2'>
                                    <span className='font-heading text-[0.65rem] tabular-nums text-tone-muted'>
                                        {pad(index + 1)}
                                    </span>

                                    <RollText text={item.label} />
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='md:col-span-2'>
                    <p className='text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted'>
                        Kontakt
                    </p>

                    <ul className='mt-4 flex flex-col gap-2 text-sm font-medium'>
                        <li>
                            <a href={`mailto:${siteConfig.email}`} className='group inline-flex'>
                                <RollText text={siteConfig.email} />
                            </a>
                        </li>

                        <li>
                            <a href={hrefFor('#bashkohu')} className='group inline-flex items-center gap-1.5'>
                                <RollText text="Bashkohu" />
                                <ArrowUpRight className='size-4' />
                            </a>
                        </li>
                    </ul>
                </div>

                <div className='col-span-2 flex flex-col items-start gap-4 text-sm md:col-span-3 md:items-end md:text-right'>
                    <button
                        type='button'
                        onClick={() => scrollToTarget(lenis, 0)}
                        className='group inline-flex items-center gap-2 border border-tone-line px-4 py-2.5 font-semibold transition-colors hover:border-tone-accent hover:text-tone-accent'
                    >
                        <ArrowUp className='size-4 transition-transform duration-500 ease-out-expo group-hover:-translate-y-0.5' />
                        <RollText text={contact.backToTop} />
                    </button>

                    <p className='text-tone-muted'>
                        {contact.credit}{' '}
                        <a
                            href={contact.creditUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-medium text-tone-fg underline-offset-4 hover:underline'
                        >
                            {contact.creditName}
                        </a>
                    </p>

                    <p className='text-tone-muted'>
                        © JAP · jap-i.com
                    </p>
                </div>
            </div>

            <p
                aria-hidden='true'
                className='pointer-events-none -mb-[2.4vw] mt-12 select-none whitespace-nowrap font-heading text-[14vw] font-black leading-[0.8] tracking-[-0.06em] text-outline [--outline-color:var(--tone-muted)] [--outline-width:1px] [font-stretch:112%] md:mt-20'
            >
                {contact.closing}
            </p>
        </footer>
    )
}
